import type { Language } from '../../i18n/translations'
import type { LegalSection } from './legalDocuments.constants'
import {
  CREDITS_SECTIONS,
  DATA_DELETION_SECTIONS,
  PRIVACY_POLICY_SECTIONS,
  TERMS_OF_SERVICE_SECTIONS,
} from './legalDocuments.constants'
import { WC2026_ORG_BILLING_TERMS_SECTION } from '../../shared/constants/wc2026OrgBillingDisclosure.constants'
import { DE_LEGAL_SECTIONS } from './legalLocalized/de.legal'
import { ES_LEGAL_SECTIONS } from './legalLocalized/es.legal'
import { FR_LEGAL_SECTIONS } from './legalLocalized/fr.legal'
import { PT_LEGAL_SECTIONS } from './legalLocalized/pt.legal'

interface LocalizedLegalSections {
  privacy: readonly LegalSection[]
  dataDeletion: readonly LegalSection[]
  terms: readonly LegalSection[]
  credits: readonly LegalSection[]
}

const ZH_PRIVACY_POLICY_SECTIONS: readonly LegalSection[] = [
  {
    title: '简介',
    paragraphs: [
      'Zporti（“我们”）运营 Zporti 移动端和网页端应用（“服务”）。本隐私政策说明你在使用服务时，我们如何收集、使用和共享信息。',
      '使用本服务即表示你同意本政策。如不同意，请不要使用本服务。',
    ],
  },
  {
    title: '我们收集的信息',
    paragraphs: [
      '账户与资料信息：例如姓名、邮箱、用户名、头像或资料图片选择，以及你在创建或更新账户时提供的类似信息。',
      '认证信息：我们使用 Supabase 认证，并可能根据平台和版本支持邮箱登录、OAuth 提供方（例如 Google 或 Facebook）以及游客会话。',
      '你创建的内容：例如体育预测、挑战、群组成员关系、好友关系、邀请，以及你通过联系或支持表单发送的信息。',
      '使用与设备信息：例如应用事件、在适用情况下由 IP 推导的大致位置、设备类型、操作系统和诊断数据，用于保障服务的安全与稳定。',
    ],
  },
  {
    title: 'Cookie、分析与同意',
    paragraphs: [
      '在网页端，我们使用必要的 cookie/存储来支持认证、安全和核心功能运行。',
      '非必要分析默认关闭，需经你同意后才会启用。你可在应用内“隐私控制”中随时更改此偏好。',
      '如已启用，分析服务商可能会处理匿名化的使用事件和有限的账户元数据，以帮助我们改进产品质量与稳定性。',
    ],
  },
  {
    title: '营销沟通',
    paragraphs: [
      '营销邮件为主动选择加入（opt-in）。仅在你明确同意后我们才会发送营销沟通。',
      '你可随时在“隐私控制”中撤回营销同意。',
    ],
  },
  {
    title: '我们如何使用信息',
    paragraphs: [
      '我们使用上述信息来提供并改进服务、验证用户身份、个性化体验、运营挑战与排行榜、发送服务通知、进行汇总分析、防止欺诈与滥用，并履行法律义务。',
    ],
  },
  {
    title: '我们如何共享信息',
    paragraphs: [
      '我们会与协助我们托管服务、发送邮件、处理分析或执行类似功能的服务提供商共享信息，并采取适当保护措施。',
      '如法律要求、为保护我们及用户权利，或在合并/资产出售等交易中（在适用时提供通知），我们可能披露信息。',
      '根据产品功能，其他用户可能看到你在应用中选择公开的信息（如显示名称、头像和与挑战相关的活动）。',
    ],
  },
  {
    title: '保留期限',
    paragraphs: [
      '我们会在你的账户处于活跃状态期间，以及为提供服务、履行法律义务、解决争议和执行协议所需的期限内保留信息。',
    ],
  },
  {
    title: '你的选择与权利',
    paragraphs: [
      '根据你所在地区，你可能享有访问、更正、删除或导出个人信息的权利，或有权反对/限制某些处理活动。',
      '应用内“隐私控制”包含账户删除申请、营销同意控制和数据导出下载（如可用）。',
      '你也可通过 info@zporti.com 联系我们。我们在回复前可能需要验证你的身份。',
    ],
  },
  {
    title: '儿童',
    paragraphs: [
      '本服务不面向 13 岁以下儿童（或你所在地区法律要求的最低年龄以下人群）。我们不会故意收集儿童个人信息。',
    ],
  },
  {
    title: '变更',
    paragraphs: [
      '我们可能会不时更新本隐私政策。更新版本会发布在本页面，并在更新时调整生效日期。',
    ],
  },
  {
    title: '联系我们',
    paragraphs: [
      '如对本政策有疑问：info@zporti.com',
    ],
  },
]

const ZH_DATA_DELETION_SECTIONS: readonly LegalSection[] = [
  {
    title: '概述',
    paragraphs: [
      '你可以请求删除与你的 Zporti 账户相关的个人数据。本页面说明如何提交请求及后续流程。',
    ],
  },
  {
    title: '如何请求删除',
    paragraphs: [
      '请在已登录状态下通过应用内“隐私控制”提交账户删除请求。',
      '或使用与你 Zporti 账户绑定的邮箱发送邮件至 info@zporti.com（或清晰提供账户邮箱及其他可用于定位账户的信息）。',
      '邮件主题请写“Data deletion request”。在处理前，我们可能要求你确认身份。',
      '你也可以在应用内“隐私控制”下载你的账户数据副本。',
    ],
  },
  {
    title: '我们会删除什么',
    paragraphs: [
      '在完成验证后，对于法律、安全或争议处理不要求保留的数据，我们会删除或匿名化与你账户相关的个人数据。',
      '部分信息可能会在备份中保留一段有限时间，直到备份轮换完成，或因法律要求必须保留。',
    ],
  },
  {
    title: '处理时效',
    paragraphs: [
      '我们会在合理期限内，并在你所在地区适用法律要求的时间范围内，回应已验证的请求。',
    ],
  },
  {
    title: '联系我们',
    paragraphs: [
      '数据删除问题：info@zporti.com',
    ],
  },
]

const ZH_TERMS_OF_SERVICE_SECTIONS: readonly LegalSection[] = [
  {
    title: '协议',
    paragraphs: [
      '本服务条款（“条款”）适用于你访问和使用由 Zporti（“我们”）运营的 Zporti 移动端和网页端应用（“服务”）。',
      '创建账户、访问或使用本服务，即表示你同意本条款。如不同意，请勿使用本服务。',
    ],
  },
  {
    title: '服务内容',
    paragraphs: [
      'Zporti 提供社交化体育预测与挑战体验：你可与好友创建或加入挑战、提交预测、查看结果和排行榜，以及使用我们后续新增或调整的相关功能。',
      '在可行情况下我们会提供合理通知，但仍可能修改、暂停或终止服务的部分功能。我们不保证服务持续不中断或绝对无错误。',
    ],
  },
  {
    title: '资格与账户',
    paragraphs: [
      '你必须满足所在地区使用本服务的最低年龄要求（通常至少 13 岁，若当地法律要求更高则以当地要求为准）。',
      '你需对所提供信息的准确性及账户凭据安全负责。如怀疑账户被未授权访问，请尽快通过下方联系方式通知我们。',
    ],
  },
  {
    title: '可接受使用',
    paragraphs: [
      '你同意不滥用本服务。例如：不得违法、骚扰或伤害他人；不得尝试未授权访问我们的系统或其他用户账户；不得抓取数据或使服务过载；不得传播恶意软件；不得作弊、操纵或破坏挑战及结果；不得发送垃圾或误导性信息。',
      '若你违反条款或使服务/用户面临风险，我们可能进行调查并暂停或终止账户。',
    ],
  },
  {
    title: '用户内容',
    paragraphs: [
      '你保留对所提交内容（如资料信息、消息和挑战活动）的权利。你授予我们非排他许可，以便在运营和改进服务所需范围内托管、存储、展示和处理该内容。',
      '你对自己提交的内容负责，并确认你有权分享该内容。我们可移除违反本条款或适用法律的内容。',
    ],
  },
  {
    title: WC2026_ORG_BILLING_TERMS_SECTION.title,
    paragraphs: [...WC2026_ORG_BILLING_TERMS_SECTION.paragraphs],
  },
  {
    title: '免责声明',
    paragraphs: [
      '在法律允许的最大范围内，本服务按“现状”和“可用”提供。体育数据、赔率和结果可能来自第三方，我们不保证其准确性或时效性。',
      '好友间的竞争与预测仅用于娱乐。本服务不构成赌博、金融或专业建议，也不适用于受监管或被禁止的相关活动。',
    ],
  },
  {
    title: '责任限制',
    paragraphs: [
      '在适用法律允许的最大范围内，对于因你使用本服务产生的间接、附带、特殊、后果性或惩罚性损害，或利润、数据、商誉损失，我们不承担责任。',
      '除法律不允许此类限制外，我们对相关索赔的总责任以以下较高者为限： (a) 索赔前 12 个月内你向我们支付的费用；或 (b) 100 美元。',
    ],
  },
  {
    title: '赔偿',
    paragraphs: [
      '在法律允许范围内，你同意就因你使用服务、你的内容或你违反本条款而引发的索赔、损失和费用（包括合理律师费），对我们及关联方、管理人员和员工进行抗辩并予以赔偿。',
    ],
  },
  {
    title: '适用法律与争议',
    paragraphs: [
      '本条款受 Zporti 运营地适用法律管辖，不考虑法律冲突规则；但若你所在国家/地区有强制消费者保护规定，则从其规定。',
      '如你为消费者，在法律要求下你可能有权在居住地法院提起争议。',
    ],
  },
  {
    title: '变更',
    paragraphs: [
      '我们可能会不时更新本条款。更新版本会发布在本页面。在法律允许范围内，更新生效后继续使用本服务即表示你接受修订条款。',
    ],
  },
  {
    title: '联系我们',
    paragraphs: [
      '条款相关问题：info@zporti.com',
    ],
  },
]

const ZH_CREDITS_SECTIONS: readonly LegalSection[] = [
  {
    title: '第三方鸣谢',
    paragraphs: [
      'Zporti 在应用内音效中使用了部分第三方音频素材。',
      '相关音频素材来源于 Freesound，并按照 CC0（公共领域）许可使用。',
    ],
  },
  {
    title: '音频来源说明',
    paragraphs: [
      'Freesound：https://freesound.org/',
      'Creative Commons CC0 1.0：https://creativecommons.org/publicdomain/zero/1.0/',
    ],
  },
]

const LOCALIZED_LEGAL_SECTIONS: Partial<Record<Language, LocalizedLegalSections>> = {
  es: ES_LEGAL_SECTIONS,
  fr: FR_LEGAL_SECTIONS,
  de: DE_LEGAL_SECTIONS,
  pt: PT_LEGAL_SECTIONS,
  zh: {
    privacy: ZH_PRIVACY_POLICY_SECTIONS,
    dataDeletion: ZH_DATA_DELETION_SECTIONS,
    terms: ZH_TERMS_OF_SERVICE_SECTIONS,
    credits: ZH_CREDITS_SECTIONS,
  },
}

export function getLocalizedLegalSections(language: Language): LocalizedLegalSections {
  return (
    LOCALIZED_LEGAL_SECTIONS[language] ?? {
      privacy: PRIVACY_POLICY_SECTIONS,
      dataDeletion: DATA_DELETION_SECTIONS,
      terms: TERMS_OF_SERVICE_SECTIONS,
      credits: CREDITS_SECTIONS,
    }
  )
}

